import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MapPin } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  formatPropertyStatus,
  formatPropertyPrice,
  formatPricePerM2,
  getPropertyPrice,
  isPriceOnRequest,
} from "../utils/formatters";
import { api } from "../services/apiClient";
import { MICROCOPY } from "../constants/microcopy";
import BackNavigationButton from "../components/navigation/BackNavigationButton";

const Favorites = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await api.get("/favorites");

      let data = null;
      try {
        data = await response.json();
      } catch (_parseError) {
        data = null;
      }

      if (!response.ok) {
        const detail = String(data?.detail || data?.message || "").toLowerCase();
        const isNoData =
          response.status === 404 ||
          ((detail.includes("no hay") || detail.includes("sin")) &&
            (detail.includes("favorito") || detail.includes("datos")));

        if (isNoData) {
          setFavorites([]);
          return;
        }

        throw new Error(data?.detail || data?.message || "Error al cargar favoritos");
      }

      setFavorites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching favorites:", error);
      toast.error(error?.message || "Error al cargar favoritos");
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (propertyId) => {
    try {
      const response = await api.delete(`/favorites/${propertyId}`);

      if (!response.ok) throw new Error("Error al eliminar favorito");

      toast.success("Eliminado de favoritos");
      fetchFavorites();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <div className="mb-4">
            <BackNavigationButton dataTestId="favorites-back" />
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold mb-4 tracking-tight"
            style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            data-testid="favorites-title"
          >
            Mis Favoritos
          </h1>
          <p className="text-muted-foreground">Propiedades que has guardado</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {MICROCOPY.emptyStates.favorites.title}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              {MICROCOPY.emptyStates.favorites.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate("/search")}>
                {MICROCOPY.emptyStates.favorites.primaryCta}
              </Button>
              <Button variant="outline" onClick={() => navigate("/developments-list")}>
                {MICROCOPY.emptyStates.favorites.secondaryCta}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((property) => (
              <div
                key={property.id || property._id}
                className="bg-background border border-border/40 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300"
                data-testid={`favorite-card-${property.id || property._id}`}
              >
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src={
                      property.image_urls?.[0] ||
                      "https://images.pexels.com/photos/35193584/pexels-photo-35193584.jpeg"
                    }
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFavorite(property.id || property._id);
                    }}
                    className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors duration-200"
                    data-testid={`remove-favorite-${property.id || property._id}`}
                  >
                    <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                  </button>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-wider font-semibold text-accent">
                      {property.category}
                    </span>
                    <span className="text-xs text-accent font-medium">
                      {formatPropertyStatus(property.status)}
                    </span>
                  </div>
                  <h3
                    className="text-xl font-bold mb-2 cursor-pointer hover:text-primary transition-colors duration-200"
                    onClick={() =>
                      navigate(`/property/${property.id || property._id}`)
                    }
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {property.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {property.address}
                  </p>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-2xl font-bold mono">
                        {formatPropertyPrice(property)}
                      </p>
                      {property.construction_size_m2 &&
                        !isPriceOnRequest(property) &&
                        getPropertyPrice(property).price && (
                          <p className="text-xs text-muted-foreground">
                            {formatPricePerM2(
                              getPropertyPrice(property).price,
                              property.construction_size_m2,
                              getPropertyPrice(property).currency,
                            )}
                          </p>
                        )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(`/property/${property.id || property._id}`)
                      }
                    >
                      Ver Detalles
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Favorites;
